import { usePage } from '@inertiajs/react'
import Routes from '@/constants/routes'
import { removeLocalePrefix } from './locale'

export const sanitizeHash = (hash: string) => {
  if (!hash) return ''
  return hash
    .trim()
    .replace(/^#/, '') // Remove beginning '#'
    .replace(/\/$/, '') // Remove trailing slash
}

// new URL().hash percent-encodes non-ASCII fragments (e.g. localized anchors),
// but element ids are the raw decoded slug. Decode so the two match.
// Guarded because decodeURIComponent throws URIError on malformed input
// (a lone '%' or bad escape), e.g. an author-supplied anchor like #50%.
export const decodeHash = (hash: string) => {
  try {
    return decodeURIComponent(hash)
  } catch {
    return hash
  }
}

export const parseURL = (url: string) => {
  // Handle protocol-relative URLs by adding https:
  if (url.startsWith('//')) {
    url = 'https:' + url
  } else if (!url.startsWith('http')) {
    url = Routes.hostname + url
  }

  try {
    const urlObj = new URL(url)
    urlObj.hash = sanitizeHash(urlObj.hash)
    return urlObj
  } catch {
    console.error('Unable to parse URL :: ' + url)
  }
  return new URL(Routes.hostname)
}

/**
 * Custom hook to parse the current URL into its components in safe SSR manner.
 * - url: The full URL string
 * - path: The URL path (without query or hash)
 * - pathWithoutLocale: The URL path without the locale prefix
 * - hash: The URL hash (without the '#' symbol)
 * - searchParams: An instance of URLSearchParams for the query parameters
 */
export function useLocation() {
  const { url } = usePage()

  // Handle cases where the hash is before the search params
  // This is non-standard but can occur in some situations
  const hashIndex = url.indexOf('#')
  const searchIndex = url.indexOf('?')
  if (hashIndex !== -1 && searchIndex !== -1 && hashIndex < searchIndex) {
    const path = url.substring(0, hashIndex)
    const hash = decodeHash(sanitizeHash(url.substring(hashIndex + 1, searchIndex)))
    const search = url.substring(searchIndex + 1)
    const searchParams = new URLSearchParams(search)
    const pathWithoutLocale = removeLocalePrefix(path)
    return { url, path, pathWithoutLocale, hash, searchParams }
  }

  const urlObject = new URL(url, Routes.hostname)
  const path = urlObject.pathname
  const hash = urlObject.hash ? decodeHash(sanitizeHash(urlObject.hash)) : ''
  const searchParams = urlObject.searchParams
  const pathWithoutLocale = removeLocalePrefix(path)

  return { url, path, pathWithoutLocale, hash, searchParams }
}
